import { h } from '@omni-torn/ui';
import {
  carRows,
  findLogCategory,
  inRange,
  keyMeets,
  lapText,
  mergeRaces,
  oldestRaceStart,
  RACE_TRACKS,
  racesCsvRows,
  racesPerDay,
  racingTotals,
  rangeText,
  readEnlistedCars,
  readMyRaces,
  readRaceLog,
  readRaceTracks,
  readRacingRecords,
  readRacingStats,
  trackOf,
  trackRows,
  type DateRange,
  type EnlistedCar,
  type LapRecord,
  type MyRace,
  type RaceLogLine,
  type RaceMeasure,
  type RaceTrack,
  type RacingStats,
  backfillCursor,
  ownEntryTime,
  pageSpan,
  type Span,
} from '@omni-torn/shared';
import { forgetLogText, ownHistoryOn, ownRead, ownSend, readingLogText } from '../own-history';
import { firstRead, LogReader, type LogStream } from '../log-kit/reader';
import { logStore, type LogStore } from '../log-kit/store';
import { csvButton, filterSelect, keptLogPanel, rangeFromAddress, rangePicker, setAddressQuery, zoomChart } from '../log-kit/ui';
import { session, torn, tornCallsLeft } from '../session';
import { box, facts, loading, lockedCard, num, problem, table, tct } from '../ui';

/**
 * Racing (You → Racing; website roadmap S37 — torn.report's racing page): per track in Torn's order (Uptown … Convict),
 * your results (places, wins, podiums, crashes), best laps (in these races and your records), the cars you drove, racing
 * skill and points — from:
 *  - Torn's racing selections with your key (Minimal): your races with every racer's result (v2 `user/races`, the
 *    newest 100; older ones one page per click on "Read older races", kept for this visit), your best laps
 *    (`user/racingrecords`), your enlisted cars (`user/enlistedcars`), racing skill, points and races all time
 *    (`personalstats?cat=racing`) and Torn's track list (`racing/tracks`, kept a day);
 *  - your log's Racing lines (Full key; Torn's "Racing" category, found by name in `torn/logcategories`, else the whole
 *    log), read into this browser by the log kit (S09): skill and points per race, and races older than Torn's list.
 * Torn calls on opening: up to 5 (+ the log's newest pages); then only on your clicks. Nothing goes to or is kept on the
 * Omni-Torn server.
 */

const DAY = 86_400_000;
const LOG_NEED = { level: 4, user: ['log'] } as const;
const MEASURES: ReadonlyArray<readonly [RaceMeasure, string]> = [
  ['races', 'Races'],
  ['wins', 'Wins'],
  ['skill', 'Racing skill gained'],
  ['points', 'Racing points'],
];
type Kind = '' | 'official' | 'custom';

/** Older pages of your races read on a click, this visit only (per player): Torn's answers, and whether Torn has no more. */
const older = new Map<number, { bodies: unknown[]; done: boolean }>();

const tile = (label: string, value: string, data = '', title = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}), ...(title ? { title } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value' }, value));
const when = (ms: number) => h('span', { title: new Date(ms).toUTCString() }, tct(ms));
const pct = (x: number | null) => (x === null ? '—' : `${Math.round(x * 100)}%`);
const skillText = (x: number | null) => (x === null ? '—' : `+${num(x, 2)}`);
const fileName = (r: DateRange) => `races-${rangeText(r).replace(/[^\w]+/g, '-').toLowerCase()}.csv`;

export async function renderRacing(view: HTMLElement): Promise<void> {
  const me = session.get();
  if (!me) return;
  const pid = me.playerId;
  const fullKey = keyMeets(me.access ?? null, LOG_NEED);
  const q = new URLSearchParams(location.search);
  let range: DateRange = rangeFromAddress('30d');
  let measure: RaceMeasure = MEASURES.find(([m]) => m === q.get('measure'))?.[0] ?? 'races';
  let trackFilter = q.get('track') ?? '';
  let kind: Kind = (['official', 'custom'] as const).find((k) => k === q.get('kind')) ?? '';
  const setQ = () => setAddressQuery(range, { measure: measure === 'races' ? null : measure, track: trackFilter || null, kind: kind || null });

  const body = h('div', { 'data-racing': '' }, loading('Reading your races from Torn…'));
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Racing'), h('p', { class: 'muted' }, ownHistoryOn() ? 'Your races per track — places, wins, best laps, cars, racing skill and points — from Torn and your own log. Your races and log are kept for you on Omni-Torn, so nothing is read twice.' : 'Your races per track — places, wins, best laps, cars, racing skill and points — from Torn and your own log. Your key only; nothing is sent to the Omni-Torn server.')),
    body,
  );

  // ------------------------------------------------------------ Torn's racing selections

  let tracks: RaceTrack[] = [...RACE_TRACKS];
  let firstPage: unknown = null;
  let records: LapRecord[] = [];
  let cars: EnlistedCar[] = [];
  let stats: RacingStats | null = null;
  const problems: string[] = [];
  const reads = await Promise.allSettled([
    torn('/v2/racing/tracks', {}, DAY),
    torn('/v2/user/races', { limit: '100', sort: 'DESC' }, 60_000),
    torn('/v2/user/racingrecords', {}, 10 * 60_000),
    torn('/v2/user/enlistedcars', {}, 10 * 60_000),
    torn('/v2/user/personalstats', { cat: 'racing' }, 10 * 60_000),
  ]);
  const [tr, rc, rr, ec, ps] = reads;
  // Terms 19: every race any device (or the server's backfill) read is kept for you — read it first; this page's own read
  // goes there too, with the stretch it covered.
  const onServer = ownHistoryOn();
  let keptBody: unknown = null;
  let keptSpans: Span[] = [];
  const raceTimes = (body: unknown) => (((body as { races?: unknown[] } | null)?.races ?? []) as unknown[]).map((r) => ownEntryTime('races', r)).filter((t): t is number => t !== null);
  if (onServer) {
    const k = await ownRead('races', { limit: 2000 }).catch(() => null);
    if (k?.on) {
      keptBody = { races: Object.values(k.entries) };
      keptSpans = k.spans;
    }
    if (rc.status === 'fulfilled') {
      const list = ((rc.value as { races?: unknown[] } | null)?.races ?? []) as unknown[];
      void ownSend('races', list, pageSpan(null, raceTimes(rc.value), 100, Date.now())).then((sp) => void (sp && (keptSpans = sp)));
    }
  }
  const why = (r: PromiseSettledResult<unknown>, what: string) => r.status === 'rejected' && problems.push(`${what} couldn’t be read: ${(r.reason as Error)?.message ?? 'no answer'}`);
  if (tr.status === 'fulfilled') tracks = readRaceTracks(tr.value);
  if (rc.status === 'fulfilled') firstPage = rc.value;
  else why(rc, 'Your races');
  if (rr.status === 'fulfilled') records = readRacingRecords(rr.value);
  else why(rr, 'Your best laps');
  if (ec.status === 'fulfilled') cars = readEnlistedCars(ec.value);
  else why(ec, 'Your cars');
  if (ps.status === 'fulfilled') stats = readRacingStats(ps.value);
  else why(ps, 'Your racing stats');
  const trackName = (id: number | null) => (id === null ? 'Not said' : (trackOf(id, tracks)?.name ?? `Track ${id}`));

  const apiRaces = (): MyRace[] => {
    const seen = new Map<number, MyRace>();
    for (const b of [firstPage, keptBody, ...(older.get(pid)?.bodies ?? [])]) for (const r of readMyRaces(b, pid)) if (r.id !== null && !seen.has(r.id)) seen.set(r.id, r);
    return [...seen.values()];
  };

  // ------------------------------------------------------------ your log (Full key)

  let store: LogStore | null = null;
  let reader: LogReader | null = null;
  let first: Promise<void> | null = null;
  let logLines: RaceLogLine[] = [];
  const logNote = h('span', { class: 'muted small', 'data-log-note': '' });
  const olderNote = h('span', { class: 'muted small', 'data-races-note': '' });

  async function ensureReader(): Promise<LogReader> {
    if (reader) return reader;
    store = await logStore(pid);
    let cat: number | null = null;
    try {
      cat = findLogCategory(await torn('/v2/torn/logcategories', {}, DAY), /^rac(e|es|ing)$/i);
    } catch {
      /* the whole log, racing lines picked out here */
    }
    const stream: LogStream = cat ? { id: `cat:${cat}`, cat } : { id: 'all' };
    reader = new LogReader(store, stream);
    return reader;
  }

  async function loadLog(): Promise<void> {
    if (!store) return;
    logLines = readRaceLog(await store.range({ from: null, to: null }), tracks);
  }

  // ------------------------------------------------------------ "Read older races" (one click = one Torn call)

  async function readOlderRaces(): Promise<string> {
    const o = older.get(pid) ?? { bodies: [], done: false };
    older.set(pid, o);
    if (o.done) return 'That’s every race Torn has for you.';
    // The server already has every race (terms 19): nothing to ask Torn.
    if (onServer && backfillCursor(keptSpans) === 0) {
      o.done = true;
      return 'No older races — that’s everything Torn has.';
    }
    if (tornCallsLeft() < 1) return 'This minute’s share of your Torn calls is used; try again in a minute.';
    const oldest = [firstPage, keptBody, ...o.bodies].map(oldestRaceStart).filter((x): x is number => x !== null);
    if (!oldest.length) {
      o.done = true;
      return 'That’s every race Torn has for you.';
    }
    const before = apiRaces().length;
    const got = await torn('/v2/user/races', { limit: '100', sort: 'DESC', to: String(Math.min(...oldest) - 1) }, 60_000);
    const n = oldestRaceStart(got) === null ? 0 : ((got as { races?: unknown[] }).races ?? []).length;
    if (onServer) {
      const sp = await ownSend('races', ((got as { races?: unknown[] } | null)?.races ?? []) as unknown[], pageSpan(Math.min(...oldest) * 1000, raceTimes(got), 100, Date.now()));
      if (sp) keptSpans = sp;
    }
    if (!n) {
      o.done = true;
      return 'No older races — that’s everything Torn has.';
    }
    o.bodies.push(got);
    const added = apiRaces().length - before;
    return `${num(added)} older race${added === 1 ? '' : 's'} read — click again for more.`;
  }

  // ------------------------------------------------------------ the report

  async function draw(): Promise<void> {
    if (fullKey) await loadLog();
    const merged = mergeRaces(apiRaces(), logLines);
    const allRaces = merged.races;
    const types: Array<readonly [string, string]> = [['', 'Every race'], ['official', 'Official'], ['custom', 'Custom']];
    const pick = (r: MyRace) => (!trackFilter || String(r.trackId ?? '') === trackFilter) && (!kind || (kind === 'official' ? r.official === true : r.official === false));
    const races = allRaces.filter(pick);
    const loose = trackFilter || kind ? [] : merged.loose;
    const shown = races.filter((r) => inRange(r.at, range));
    const now = Date.now();
    const reread = () => void draw();
    const onRange = (nr: DateRange) => {
      range = nr;
      setQ();
      reread();
    };

    // Your log panel (Full key) — or what a Full key would add.
    let kept: HTMLElement;
    if (!fullKey) kept = lockedCard('From your log', LOG_NEED, 'your log adds racing skill and points per race, and races older than Torn’s list reaches.');
    else if (!reader || !store) kept = loading(readingLogText());
    else
      kept = await keptLogPanel({
        reader,
        store,
        range,
        matching: logLines.length,
        what: 'racing lines',
        note: logNote,
        forgetText: forgetLogText(),
        onForget: () => {
          reader = null;
          store = null;
          first = null;
          logLines = [];
          void startLog();
        },
        redraw: reread,
      });

    const filters = h(
      'div',
      { class: 'toolbar' },
      filterSelect('Track', [['', 'Every track'], ...tracks.map((t) => [String(t.id), t.name] as const)], trackFilter, (v) => {
        trackFilter = v;
        setQ();
        reread();
      }, 'track'),
      filterSelect('Kind of race', types, kind, (v) => {
        kind = v as Kind;
        setQ();
        reread();
      }, 'kind'),
    );

    const t = racingTotals(races, loose, range);
    const tiles = h(
      'div',
      { class: 'tiles' },
      tile('Races', num(t.races), 'races'),
      tile('Wins', `${num(t.wins)} (${pct(t.winRate)})`, 'wins'),
      tile('Podiums', num(t.podiums), 'podiums', 'Finished 1st, 2nd or 3rd'),
      tile('Crashes', num(t.crashes), 'crashes'),
      tile('Average place', t.avgPosition === null ? '—' : num(t.avgPosition, 1), 'avg'),
      tile('Skill gained', fullKey ? skillText(t.skill) : '—', 'skill', 'From your log’s racing lines'),
      tile('Points earned', fullKey ? num(t.points) : '—', 'points', 'From your log’s racing lines'),
    );
    const allTime = stats
      ? box('All time (Torn’s counters)', facts([
          ['Racing skill now', num(stats.skill, 2)],
          ['Racing points', num(stats.points)],
          ['Races entered', num(stats.entered)],
          ['Races won', stats.entered && stats.won !== null ? `${num(stats.won)} (${pct(stats.won / stats.entered)})` : num(stats.won)],
        ]), { id: 'racing-all-time' })
      : null;

    const measureSel = filterSelect('Chart', MEASURES, measure, (v) => {
      measure = v as RaceMeasure;
      setQ();
      reread();
    }, 'measure');
    const label = MEASURES.find(([m]) => m === measure)![1];
    const chart = box(h('h2', {}, `${label} per day`), [zoomChart(racesPerDay(races, loose, measure, range, now), { label, ...(measure === 'skill' ? { format: (x: number) => num(x, 2) } : { format: (x: number) => (Number.isInteger(x) ? num(x) : '') }), onZoom: onRange })], { id: 'racing-chart', extra: measureSel });

    const tRows = trackRows(races, range, tracks, trackFilter ? records.filter((r) => String(r.trackId) === trackFilter) : records).filter((r) => !trackFilter || String(r.trackId ?? '') === trackFilter);
    const trackBox = box('Per track', [
      table(tRows, [
        { label: 'Track', get: (r) => (r.races ? r.name : h('span', { class: 'muted' }, r.name)), sort: (r) => tRows.indexOf(r) },
        { label: 'Races', get: (r) => (r.races ? num(r.races) : '—'), sort: (r) => r.races, cls: 'r' },
        { label: 'Wins', get: (r) => (r.races ? num(r.wins) : '—'), sort: (r) => r.wins, cls: 'r' },
        { label: 'Podiums', get: (r) => (r.races ? num(r.podiums) : '—'), sort: (r) => r.podiums, cls: 'r' },
        { label: 'Avg place', get: (r) => (r.avgPosition === null ? '—' : num(r.avgPosition, 1)), sort: (r) => r.avgPosition ?? 999, cls: 'r' },
        { label: 'Best', get: (r) => (r.bestPosition === null ? '—' : num(r.bestPosition)), sort: (r) => r.bestPosition ?? 999, cls: 'r' },
        { label: 'Crashes', get: (r) => (r.crashes ? num(r.crashes) : '—'), sort: (r) => r.crashes, cls: 'r' },
        { label: 'Best lap', get: (r) => (r.bestLap === null ? '—' : h('span', { title: r.bestLapCar ?? '' }, lapText(r.bestLap))), sort: (r) => r.bestLap ?? 1e9, cls: 'r' },
        { label: 'Your record', get: (r) => (r.recordLap === null ? '—' : `${lapText(r.recordLap)}${r.recordCar ? ` · ${r.recordCar}` : ''}`), sort: (r) => r.recordLap ?? 1e9 },
        ...(fullKey ? [{ label: 'Skill', get: (r: (typeof tRows)[number]) => (r.skill ? skillText(r.skill) : '—'), sort: (r: (typeof tRows)[number]) => r.skill, cls: 'r' }, { label: 'Points', get: (r: (typeof tRows)[number]) => (r.points ? num(r.points) : '—'), sort: (r: (typeof tRows)[number]) => r.points, cls: 'r' }] : []),
        { label: 'Last', get: (r) => (r.last ? tct(r.last) : '—'), sort: (r) => r.last ?? 0 },
      ], { initial: 0, desc: false, empty: 'No tracks.' }),
      h('p', { class: 'muted small' }, 'Torn’s tracks in Torn’s order. “Best lap” is your fastest lap in the races shown; “Your record” is Torn’s record of your fastest lap there with each car (all time).'),
    ], { id: 'racing-tracks' });

    const cRows = carRows(races, range, cars);
    const carBox = box('Cars you raced', [
      table(cRows, [
        { label: 'Car', get: (r) => r.car, sort: (r) => r.car },
        { label: 'Class', get: (r) => r.carClass ?? '—', sort: (r) => r.carClass ?? '~' },
        { label: 'Races', get: (r) => num(r.races), sort: (r) => r.races, cls: 'r' },
        { label: 'Wins', get: (r) => num(r.wins), sort: (r) => r.wins, cls: 'r' },
        { label: 'Podiums', get: (r) => num(r.podiums), sort: (r) => r.podiums, cls: 'r' },
        { label: 'Crashes', get: (r) => (r.crashes ? num(r.crashes) : '—'), sort: (r) => r.crashes, cls: 'r' },
        { label: 'Tracks', get: (r) => num(r.tracks), sort: (r) => r.tracks, cls: 'r' },
        { label: 'Best', get: (r) => (r.bestPosition === null ? '—' : num(r.bestPosition)), sort: (r) => r.bestPosition ?? 999, cls: 'r' },
        { label: 'All time (won / entered)', get: (r) => (r.entered === null ? '—' : `${num(r.won)} / ${num(r.entered)}`), sort: (r) => r.entered ?? -1, cls: 'r' },
      ], { initial: 2, empty: 'No races in this range.' }),
      cars.length ? h('p', { class: 'muted small' }, `Your enlisted cars: ${cars.map((c) => `${c.name}${c.name !== c.model ? ` (${c.model})` : ''} — ${num(c.won)} won of ${num(c.entered)}`).join(' · ')}.`) : null,
    ].filter(Boolean) as HTMLElement[], { id: 'racing-cars' });

    const o = older.get(pid);
    const olderBtn = h('button', { type: 'button', class: 'btn-small', 'data-action': 'races-older', ...(o?.done ? { disabled: true } : {}) }, o?.done ? 'Every race read' : 'Read older races') as HTMLButtonElement;
    olderBtn.addEventListener('click', async () => {
      olderBtn.disabled = true;
      olderNote.textContent = ' Reading…';
      try {
        olderNote.textContent = await readOlderRaces();
      } catch (err) {
        olderNote.replaceChildren(problem(err));
      }
      reread();
    });
    const list = shown;
    const csv = csvButton(() => fileName(range), () => racesCsvRows(list, trackName));
    const placeCell = (r: MyRace) => (r.crashed ? h('span', { class: 't-red' }, 'crashed') : r.position === null ? '—' : h('span', { class: r.position === 1 ? 't-green' : '' }, `${num(r.position)}${r.racers ? ` of ${num(r.racers)}` : ''}`));
    const racesBox = box('Your races', [
      h('div', { class: 'toolbar' }, olderBtn, olderNote),
      table(list.slice(0, 500), [
        { label: 'When', get: (r) => when(r.at), sort: (r) => r.at },
        { label: 'Track', get: (r) => trackName(r.trackId), sort: (r) => trackName(r.trackId) },
        { label: 'Place', get: placeCell, sort: (r) => (r.crashed ? 99 : (r.position ?? 98)), cls: 'r' },
        { label: 'Car', get: (r) => r.car ?? '—', sort: (r) => r.car ?? '~' },
        { label: 'Time', get: (r) => (r.time === null ? '—' : lapText(r.time)), sort: (r) => r.time ?? 1e9, cls: 'r' },
        { label: 'Best lap', get: (r) => lapText(r.bestLap), sort: (r) => r.bestLap ?? 1e9, cls: 'r' },
        ...(fullKey ? [{ label: 'Skill', get: (r: MyRace) => skillText(r.skill), sort: (r: MyRace) => r.skill ?? -1, cls: 'r' }, { label: 'Points', get: (r: MyRace) => (r.points === null ? '—' : num(r.points)), sort: (r: MyRace) => r.points ?? -1, cls: 'r' }] : []),
        { label: 'Race', get: (r) => h('span', { class: 'small' }, `${r.title}${r.official === false ? ' (custom)' : ''}${r.from === 'log' ? ' · from your log' : ''}`), sort: (r) => r.title },
      ], { csv: false, initial: 0, empty: 'No races in this range.' }),
      list.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(list.length)} — the CSV has them all.`) : null,
      h('p', { class: 'muted small' }, 'Torn gives your newest 100 races at a time; “Read older races” asks for the next 100 (one call a click, kept for this visit). Races older still come from your log when it has them.'),
    ].filter(Boolean) as HTMLElement[], { id: 'racing-races', extra: csv });

    const odd = fullKey && !trackFilter && !kind ? logLines.filter((l) => (l.kind === 'join' || l.kind === 'leave' || l.kind === 'car' || l.kind === 'other') && inRange(l.at, range)) : [];
    const otherBox = odd.length
      ? box(`Other racing lines (${num(odd.length)})`, [
          table(odd.slice(-100).reverse(), [
            { label: 'When', get: (l) => when(l.at), sort: (l) => l.at },
            { label: 'Line', get: (l) => l.title },
          ], { initial: 0 }),
          h('p', { class: 'muted small' }, 'Racing lines that aren’t a race’s end, skill or points (joining, leaving, cars and parts) — as Torn wrote them, not counted.'),
        ], { id: 'racing-other' })
      : null;

    const notes = h('p', { class: 'muted small', 'data-racing-notes': '' }, 'How it’s read: places, cars and laps come from Torn’s race results; racing skill and points per race from your log’s racing lines (Torn doesn’t document them, so they’re read by title and field — a skill or points line counts for the race it names, or the race that ended in the 10 minutes before it). Nothing is guessed: what a line doesn’t say stays blank.');
    body.replaceChildren(...([
      problems.length ? h('div', { class: 'notice bad', role: 'alert' }, problems.join(' · ')) : null,
      kept,
      rangePicker(range, onRange),
      filters,
      tiles,
      allTime,
      chart,
      trackBox,
      carBox,
      racesBox,
      otherBox,
      notes,
    ].filter(Boolean) as HTMLElement[]));
  }

  async function startLog(): Promise<void> {
    if (!fullKey) return;
    try {
      const r = await ensureReader();
      first ??= firstRead(r).then(
        (text) => void (logNote.textContent = text),
        (err: unknown) => void logNote.replaceChildren(problem(err)),
      );
      await first;
      if (body.isConnected) await draw();
    } catch (err) {
      logNote.replaceChildren(problem(err));
    }
  }

  await draw();
  void startLog();
}
